import PatientIcon from "@mui/icons-material/Accessible";
import AppointmentIcon from "@mui/icons-material/EditCalendar";
import CalenderIcon from "@mui/icons-material/Event";
import GroupsIcon from "@mui/icons-material/Groups";
import DoctorIcon from "@mui/icons-material/Person";
import {
  Box,
  Button,
  ButtonBase,
  Grid,
  Menu,
  Radio,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { ReactNode, useState } from "react";
import CustomDataGridV2 from "../../../components/CustomDataGridV2";
import Flex from "../../../components/Flex";
import ToggleSwitch from "../../../components/ToggleSwitch";
import DashBoardCard from "../../../components/app/admin/dashboard/DashBoardCard";
import {
  DASHBOARD_DATA,
  DashboardDataType,
} from "../../../dummy-data/dashboard-constant";
import { DEFAULT_PAGE_SIZE } from "../../../utils/utils";

interface DescType {
  incDesc: boolean;
  cardDescription: string;
}

interface CardProps {
  id: number;
  imageGroupSrc: ReactNode;
  cardTitle: number;
  heading: string;
  description: DescType;
}

const COLUMNS: GridColDef<DashboardDataType>[] = [
  {
    field: "groupId",
    headerName: "Group ID",
    width: 180,
    sortable: false,
    disableColumnMenu: true,
    headerClassName: "header-theme",
  },
  {
    field: "providerGroupName",
    headerName: "Provider Group Name",
    minWidth: 280,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    headerClassName: "header-theme",
    renderCell: ({ value }: GridRenderCellParams) => (
      <Tooltip title={value}>
        <ButtonBase>
          <Typography fontSize={"18px"}>{value}</Typography>
        </ButtonBase>
      </Tooltip>
    ),
  },
  {
    field: "speciality",
    headerName: "Speciality",
    disableColumnMenu: true,
    minWidth: 280,
    flex: 1,
    sortable: false,
    headerClassName: "header-theme",
    renderCell: ({ value }: GridRenderCellParams) => (
      <Tooltip title={value}>
        <ButtonBase>
          <Typography fontSize={"18px"}>{value}</Typography>
        </ButtonBase>
      </Tooltip>
    ),
  },
  {
    field: "providerCount",
    headerName: "Provider Count",
    width: 200,
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    sortable: false,
    headerClassName: "header-theme",
  },
  {
    field: "patientsCount",
    headerName: "Patients Count",
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    width: 200,
    sortable: false,
    headerClassName: "header-theme",
  },
  {
    field: "appointmentCount",
    headerName: "Appointment Count",
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    width: 200,
    sortable: false,
    headerClassName: "header-theme",
  },
  {
    field: "encounterCount",
    headerName: "Visit Count",
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    width: 250,
    sortable: false,
    headerClassName: "header-theme",
  },
  {
    field: "status",
    headerName: "Status",
    disableColumnMenu: true,
    width: 170,
    sortable: false,
    headerClassName: "header-theme",
    renderCell: ({ row, value }) => (
      <ToggleSwitch id={row.id.toString()} checked={value === "Active"} />
    ),
  },
];

const CardData: CardProps[] = [
  {
    id: 1,
    imageGroupSrc: (
      <GroupsIcon sx={{ fontSize: "3em", color: "secondary.main" }} />
    ),
    cardTitle: 326,
    heading: "Provider Groups",
    description: {
      incDesc: true,
      cardDescription: "5% higher than last month",
    },
  },
  {
    id: 2,
    imageGroupSrc: (
      <DoctorIcon sx={{ fontSize: "3em", color: "secondary.main" }} />
    ),
    cardTitle: 426,
    heading: "Providers",
    description: {
      incDesc: true,
      cardDescription: "5% higher than last month",
    },
  },
  {
    id: 3,
    imageGroupSrc: (
      <PatientIcon sx={{ fontSize: "3em", color: "secondary.main" }} />
    ),
    cardTitle: 656,
    heading: "Patients",
    description: {
      incDesc: true,
      cardDescription: "5% higher than last month",
    },
  },
  {
    id: 4,
    imageGroupSrc: (
      <AppointmentIcon sx={{ fontSize: "3em", color: "secondary.main" }} />
    ),
    cardTitle: 48,
    heading: "Appointments",
    description: {
      incDesc: true,
      cardDescription: "5% higher than last month",
    },
  },
  {
    id: 5,
    imageGroupSrc: (
      <CalenderIcon sx={{ fontSize: "3em", color: "secondary.main" }} />
    ),
    cardTitle: 35,
    heading: "Visits",
    description: {
      incDesc: false,
      cardDescription: "5% lower than last month",
    },
  },
];

const AdminDashboard = () => {
  return (
    <Box height={"100%"}>
      <CardRow />
      <Box mt={"15px"} height={"96%"}>
        <CustomDataGridV2
          rows={DASHBOARD_DATA}
          columns={COLUMNS}
          disableRowSelectionOnClick
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: DEFAULT_PAGE_SIZE,
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

const CardRow = () => {
  const [selectedValue, setSelectedValue] = useState<string>("");
  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSelectedValue(
      event.target.value == selectedValue ? "" : event.target.value
    );
  }
  return (
    <Box>
      <Flex
        justifyContent={"space-between"}
        marginBottom={"30px"}
        position={"fixed"}
        top={"60px"}
        left={"16px"}
        right={"16px"}
        zIndex={1000}
        paddingX={"14px"}
        paddingY={"10px"}
        bgcolor={"white"}
      >
        <Typography variant="h5" color={"primary.main"}>
          Dashboard
        </Typography>
        <PopupState variant="popover" popupId="user-button-menu">
          {(menuList) => (
            <Box>
              <Button
                variant="outlined"
                startIcon={<CalenderIcon />}
                {...bindTrigger(menuList)}
              >
                <Typography>Date Range</Typography>
              </Button>

              <Menu {...bindMenu(menuList)}>
                <Typography
                  padding={"5px"}
                  marginLeft={"10px"}
                  fontSize={"18px"}
                  color={"#1A1A1A"}
                >
                  Calender Range
                </Typography>
                <Flex sx={{ width: "303px", padding: "5px" }}>
                  <Radio
                    checked={selectedValue === "month"}
                    onChange={handleChange}
                    value="month"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "Month" }}
                  />
                  <Typography marginTop={"10px"}>Month</Typography>
                </Flex>
                {selectedValue === "month" && (
                  <Box
                    sx={{
                      "& .date-range-select": {
                        font: "normal normal medium 11px/13px Roboto",
                        fontFamily: "Roboto",
                      },
                    }}
                  >
                    date range
                  </Box>
                )}
                <Flex sx={{ width: "303px", padding: "5px" }}>
                  <Radio
                    checked={selectedValue === "all-time"}
                    onChange={handleChange}
                    value="all-time"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "All Time" }}
                  />
                  <Typography marginTop={"10px"}>All Time</Typography>
                </Flex>
                <Box sx={{ marginLeft: "15px" }}>
                  <Button
                    variant="contained"
                    sx={{ background: selectedValue ? "" : "gray" }}
                  >
                    Apply
                  </Button>
                </Box>
              </Menu>
            </Box>
          )}
        </PopupState>
      </Flex>

      <Grid container spacing={3} marginTop={"40px"} p={"2px"}>
        {CardData.map((item: CardProps) => {
          return (
            <Grid item key={item.id} sm={6} md={4} lg={2.4}>
              <DashBoardCard
                key={item.id}
                imageGroupSrc={item.imageGroupSrc}
                cardTitle={item.cardTitle}
                heading={item.heading}
                description={item.description}
                cardHeight={"150px"}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
export default AdminDashboard;
